/**
 * CetusEd Community Portal — Notification Preferences
 *
 * COMPLIANCE: IDEA §300.322 — Schools must give parents adequate notice
 *   of IEP meetings. Parents may select their preferred notification
 *   methods (email, SMS). The Telephone Consumer Protection Act (TCPA)
 *   requires express consent for automated SMS notifications.
 *
 *   FERPA §1232g(b)(4)(B) — Notification preferences are not student
 *   education records, but the delivery of notice becomes part of the
 *   procedural record for due process hearings.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '../../api/hooks';
import { Button } from '../../ui/components/Button';
import { Card } from '../../ui/components/Card';
import { LoadingSpinner } from '../../ui/components/LoadingSpinner';
import type { NotificationPreferences } from '../../api/hooks';

const DEFAULT_PREFERENCES: NotificationPreferences = {
  email: {
    newDocuments: true,
    messages: true,
    meetingReminders: true,
    consentRequests: true,
  },
  sms: {
    meetingReminders: false,
    urgentAlerts: false,
  },
};

export const NotificationSettings: React.FC = () => {
  const { data: prefs, isLoading, error } = useNotificationPreferences();
  const updatePrefs = useUpdateNotificationPreferences();

  const [formState, setFormState] = useState<NotificationPreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    if (prefs) {
      setFormState(prefs);
    }
  }, [prefs]);

  const handleEmailToggle = useCallback((key: keyof NotificationPreferences['email']) => {
    setFormState((prev) => ({
      ...prev,
      email: { ...prev.email, [key]: !prev.email[key] },
    }));
  }, []);

  const handleSMSToggle = useCallback((key: keyof NotificationPreferences['sms']) => {
    setFormState((prev) => ({
      ...prev,
      sms: { ...prev.sms, [key]: !prev.sms[key] },
    }));
  }, []);

  const handleSave = useCallback(() => {
    // mutate() routes failures into updatePrefs.isError, which is rendered
    // next to the save button — no swallowed rejection, no invisible failure.
    updatePrefs.mutate(formState);
  }, [formState, updatePrefs]);

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="error-page">
        <h1>Preferences Not Available</h1>
        <p>Notification preferences could not be loaded.</p>
        <Link to="/profile">Back to Profile</Link>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <nav className="breadcrumb">
        <Link to="/profile">Profile</Link> / Notification Preferences
      </nav>

      <h1>Notification Preferences</h1>

      <Card title="Email Notifications">
        <div className="preferences-list">
          <label className="preference-row">
            <input
              type="checkbox"
              checked={formState.email.newDocuments}
              onChange={() => handleEmailToggle('newDocuments')}
            />
            <span>New documents available for review</span>
          </label>
          <label className="preference-row">
            <input
              type="checkbox"
              checked={formState.email.messages}
              onChange={() => handleEmailToggle('messages')}
            />
            <span>New messages from school staff</span>
          </label>
          <label className="preference-row">
            <input
              type="checkbox"
              checked={formState.email.meetingReminders}
              onChange={() => handleEmailToggle('meetingReminders')}
            />
            <span>Meeting reminders and schedule changes</span>
          </label>
          <label className="preference-row">
            <input
              type="checkbox"
              checked={formState.email.consentRequests}
              onChange={() => handleEmailToggle('consentRequests')}
            />
            <span>Consent form requests</span>
          </label>
        </div>
      </Card>

      <Card title="SMS Notifications">
        <p className="card-hint">
          Standard message and data rates may apply. You can opt out at any time.
        </p>
        <div className="preferences-list">
          <label className="preference-row">
            <input
              type="checkbox"
              checked={formState.sms.meetingReminders}
              onChange={() => handleSMSToggle('meetingReminders')}
            />
            <span>Meeting reminders via SMS</span>
          </label>
          <label className="preference-row">
            <input
              type="checkbox"
              checked={formState.sms.urgentAlerts}
              onChange={() => handleSMSToggle('urgentAlerts')}
            />
            <span>Urgent alerts (schedule changes, cancellations)</span>
          </label>
        </div>
      </Card>

      {updatePrefs.isError && (
        <div className="form-error" role="alert">
          Failed to save notification preferences. Please try again.
        </div>
      )}

      <div className="form-actions">
        <Button onClick={handleSave} isLoading={updatePrefs.isPending}>
          Save Preferences
        </Button>
        <Link to="/profile">
          <Button variant="ghost">Cancel</Button>
        </Link>
      </div>
    </div>
  );
};

export { NotificationSettings as Notifications };
